import { AfterViewInit, Component, createNgModule, inject, Injector, OnDestroy, OnInit, Type, ViewChild, ViewContainerRef } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, Subject, takeUntil, tap } from 'rxjs';
import { StartupConfigService } from 'projects/config/startup-config.service';
import { toggleSplashScreenVisibility } from 'projects/lib-shared-common/src/public-api';
import { BaseLayoutConfig } from './base-layout.config';

@Component({ template: '' })
export abstract class BaseLayoutComponent<LayoutType, LayoutComponentType> implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('layoutComponentRef', { read: ViewContainerRef })
  protected layoutComponentRef: ViewContainerRef;

  protected _injector = inject(Injector);
  protected _router = inject(Router);
  protected _activatedRoute = inject(ActivatedRoute);
  protected _startupConfigService = inject(StartupConfigService);

  protected currentLayout: LayoutType;
  protected _unsubscribeAll: Subject<any> = new Subject<any>();

  protected abstract get defaultLayoutConfig(): BaseLayoutConfig<LayoutType>;

  constructor() {
    this._startupConfigService.initialized$
      .pipe(
        tap((initialized) => {
          if (initialized) {
            this.onStartupInitialized();
          }
        })
      )
      .subscribe();
  }

  /** Called when the dictionary is loaded. Layouts that render their child lazily can delay the splash. */
  protected onStartupInitialized(): void {
    toggleSplashScreenVisibility();
  }

  public ngOnInit(): void {
    this._updateLayout();
    this._router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this._unsubscribeAll),
        tap(() => this._updateLayout())
      )
      .subscribe();

    window['getLanguage'] = () => {
      return this.getLanguage();
    };
  }

  public ngAfterViewInit(): void {
    this._loadLayoutFromCurrent();
  }

  protected getLanguage(): string {
    return 'en-US';
  }

  public abstract childLayoutWasResolved(): void;
  protected abstract getLayoutImportPromiseFnByLayout(layout: LayoutType): () => Promise<{ component: Type<LayoutComponentType>; module: Type<any> }>;

  private _updateLayout(): void {
    let route = this._activatedRoute;
    while (route.firstChild) {
      route = route.firstChild;
    }

    let newLayout = this.currentLayout;

    const layoutFromQueryParam = route.snapshot.queryParamMap.get('layout') as LayoutType;
    if (layoutFromQueryParam) {
      newLayout = layoutFromQueryParam;
    }

    route.pathFromRoot.forEach((path) => {
      if (path.routeConfig && path.routeConfig.data && path.routeConfig.data.layout) {
        newLayout = path.routeConfig.data.layout;
      }
    });

    newLayout = newLayout ?? this.defaultLayoutConfig.layout;

    if (newLayout !== this.currentLayout) {
      this.currentLayout = newLayout;
      this._loadLayoutFromCurrent();
    }
  }

  private async _loadLayoutFromCurrent() {
    if (!this.layoutComponentRef || this.layoutComponentRef.length > 0) {
      return;
    }

    const layout = this.currentLayout;
    const { component, module } = await this.getLayoutImportPromiseFnByLayout(layout)();
    if (layout === this.currentLayout && this.layoutComponentRef && this.layoutComponentRef.length === 0) {
      const moduleRef = createNgModule(module, this._injector);
      this.layoutComponentRef.createComponent(component, { ngModuleRef: moduleRef });
    }
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }
}
